import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, PenTool, MessageSquare, Search, ChevronDown, ChevronUp,
  User, Calendar, Phone, MapPin, Paperclip, Download, Edit3, X,
  CheckCircle, Clock, AlertCircle, ArrowRight, Filter, RefreshCw,
  Users, Send, UserPlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientRequestsAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    case_requests: [],
    notary_requests: [],
    consultation_requests: []
  });
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('cases');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // نافذة تحديث الحالة
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [updating, setUpdating] = useState(false);

  // نافذة الإحالة
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [assignNote, setAssignNote] = useState('');

  const statusOptions = [
    { value: 'جديد', label: 'جديد', color: 'bg-blue-100 text-blue-700' },
    { value: 'قيد المراجعة', label: 'قيد المراجعة', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'تمت الإحالة للمختص', label: 'تمت الإحالة للمختص', color: 'bg-green-100 text-green-700' },
    { value: 'مكتمل', label: 'مكتمل', color: 'bg-gray-100 text-gray-700' },
  ];

  useEffect(() => {
    fetchData();
    fetchEmployees();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/client-requests/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching requests:', err);
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusInfo = statusOptions.find(s => s.value === status) || 
      { label: status || 'جديد', color: 'bg-gray-100 text-gray-700' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getRoleName = (role, department) => {
    if (department === 'مستشار') return 'مستشار';
    const roles = {
      'admin': 'مدير',
      'lawyer': 'محامي',
      'staff': 'موظف',
      'accountant': 'محاسب'
    };
    return roles[role] || role;
  };

  const downloadAttachment = (attachment) => {
    try {
      const byteCharacters = atob(attachment.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.type });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
    }
  };

  const openStatusModal = (request) => {
    setSelectedRequest(request);
    setNewStatus(request.status || 'جديد');
    setStatusNote('');
    setShowStatusModal(true);
  };

  const openAssignModal = (request) => {
    setSelectedRequest(request);
    setSelectedEmployees(request.assigned_to || []);
    setAssignNote('');
    setShowAssignModal(true);
  };

  const updateStatus = async () => {
    if (!selectedRequest || !newStatus) return;
    
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/client-requests/${selectedRequest.id}/status`,
        { status: newStatus, note: statusNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowStatusModal(false);
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('حدث خطأ أثناء تحديث الحالة');
    }
    setUpdating(false);
  };

  const assignRequest = async () => {
    if (!selectedRequest || selectedEmployees.length === 0) {
      alert('يرجى اختيار موظف واحد على الأقل');
      return;
    }
    
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/client-requests/${selectedRequest.id}/assign`,
        { assigned_to: selectedEmployees, note: assignNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowAssignModal(false);
      fetchData();
      alert('تمت الإحالة بنجاح');
    } catch (err) {
      console.error('Error assigning request:', err);
      alert('حدث خطأ أثناء الإحالة');
    }
    setUpdating(false);
  };

  const toggleEmployeeSelection = (employeeId) => {
    if (selectedEmployees.includes(employeeId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== employeeId));
    } else {
      setSelectedEmployees([...selectedEmployees, employeeId]);
    }
  };

  const filterRequests = (requests) => {
    return requests.filter(req => {
      const matchesSearch = 
        req.request_number?.includes(searchQuery) ||
        req.client_name?.includes(searchQuery) ||
        req.client_national_id?.includes(searchQuery) ||
        req.title?.includes(searchQuery) ||
        req.subject?.includes(searchQuery) ||
        req.description?.includes(searchQuery);
      
      const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  const getCurrentRequests = () => {
    switch (activeTab) {
      case 'cases':
        return filterRequests(data.case_requests);
      case 'notary':
        return filterRequests(data.notary_requests);
      case 'consultations':
        return filterRequests(data.consultation_requests);
      default:
        return [];
    }
  };

  // مكون صف الجدول المشترك
  const RequestRow = ({ req, showCityColumn = false }) => (
    <React.Fragment>
      <tr 
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpandedRequest(expandedRequest === req.id ? null : req.id)}
      >
        <td className="px-3 py-3 font-mono text-primary text-sm">{req.client_national_id || '-'}</td>
        <td className="px-3 py-3 font-mono text-gold font-semibold text-sm">{req.request_number}</td>
        <td className="px-3 py-3 text-sm">{req.created_at_hijri || '-'}</td>
        <td className="px-3 py-3 text-sm">{formatDate(req.created_at)}</td>
        <td className="px-3 py-3 text-sm">
          <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
            {req.case_type || req.service_type || req.subject || '-'}
          </span>
        </td>
        {showCityColumn && (
          <td className="px-3 py-3 text-sm">
            {req.city_name && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-gray-400" />
                {req.city_name}
              </span>
            )}
          </td>
        )}
        <td className="px-3 py-3 text-sm max-w-[150px] truncate">{req.title || req.description || req.message || '-'}</td>
        <td className="px-3 py-3">
          {req.attachments?.length > 0 && (
            <span className="flex items-center gap-1 text-blue-600 text-sm">
              <Paperclip className="w-4 h-4" />
              {req.attachments.length}
            </span>
          )}
        </td>
        <td className="px-3 py-3">
          {req.assigned_to_names?.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {req.assigned_to_names.slice(0, 2).map((name, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                  {name}
                </span>
              ))}
              {req.assigned_to_names.length > 2 && (
                <span className="text-xs text-gray-500">+{req.assigned_to_names.length - 2}</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400">لم تتم الإحالة</span>
          )}
        </td>
        <td className="px-3 py-3">{getStatusBadge(req.status)}</td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); openAssignModal(req); }}
              className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="إحالة لموظف"
            >
              <UserPlus className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); openStatusModal(req); }}
              className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors"
              title="تعديل الحالة"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      {expandedRequest === req.id && (
        <tr>
          <td colSpan={showCityColumn ? 11 : 10} className="px-4 py-4 bg-gray-50">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-gray-500 text-xs mb-1">اسم العميل:</p>
                <p className="font-semibold">{req.client_name}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-1">رقم الجوال:</p>
                <p className="font-mono" dir="ltr">{req.phone_number || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-1">المحال إليهم:</p>
                <p className="text-sm">{req.assigned_to_names?.join('، ') || 'لم تتم الإحالة'}</p>
              </div>
              <div className="col-span-2 md:col-span-3">
                <p className="text-gray-500 text-xs mb-1">التفاصيل:</p>
                <p className="bg-white p-3 rounded border text-sm">{req.description || req.message || '-'}</p>
              </div>
              {req.attachments?.length > 0 && (
                <div className="col-span-2 md:col-span-3">
                  <p className="text-gray-500 text-xs mb-2">المرفقات:</p>
                  <div className="flex flex-wrap gap-2">
                    {req.attachments.map((att, idx) => (
                      <button
                        key={idx}
                        onClick={() => downloadAttachment(att)}
                        className="flex items-center gap-2 px-3 py-2 bg-white border rounded hover:bg-gray-50"
                      >
                        <Paperclip className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{att.name}</span>
                        <Download className="w-4 h-4 text-blue-500" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {req.status_history?.length > 0 && (
                <div className="col-span-2 md:col-span-3">
                  <p className="text-gray-500 text-xs mb-2">سجل التحديثات:</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {req.status_history.map((history, idx) => (
                      <div key={idx} className="bg-white p-2 rounded border text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{history.status}</span>
                          <span className="text-xs text-gray-500">{formatDateTime(history.updated_at)}</span>
                        </div>
                        {history.note && <p className="text-gray-600 mt-1">{history.note}</p>}
                        <p className="text-xs text-gray-400 mt-1">بواسطة: {history.updated_by_name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  // جدول موحد
  const RequestsTable = ({ requests, showCityColumn = false }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">رقم العميل</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">رقم الطلب</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">هجري</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">ميلادي</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">النوع</th>
            {showCityColumn && <th className="px-3 py-3 text-right font-semibold text-gray-600">موقع العقار</th>}
            <th className="px-3 py-3 text-right font-semibold text-gray-600">الموضوع</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">مرفقات</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">المحال إليهم</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">الحالة</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600">إجراءات</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {requests.map((req) => (
            <RequestRow key={req.id} req={req} showCityColumn={showCityColumn} />
          ))}
        </tbody>
      </table>
      {requests.length === 0 && (
        <div className="text-center py-8 text-gray-500">لا توجد طلبات</div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-gold mx-auto mb-4" />
          <p className="text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl">
      {/* Header */}
      <header className="bg-primary text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-white/80 hover:text-white"
            >
              <ArrowRight className="w-5 h-5" />
              العودة للوحة التحكم
            </button>
            <h1 className="text-xl font-heading font-bold">طلبات العملاء</h1>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            تحديث
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* الإحصائيات */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-lg border-r-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">طلبات القضايا</p>
                <p className="text-2xl font-bold text-primary">{data.total_cases || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-lg border-r-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">طلبات الموثق</p>
                <p className="text-2xl font-bold text-primary">{data.total_notary || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <PenTool className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-lg border-r-4 border-gold">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">الاستشارات</p>
                <p className="text-2xl font-bold text-primary">{data.total_consultations || 0}</p>
              </div>
              <div className="p-3 bg-gold/20 rounded-lg">
                <MessageSquare className="w-6 h-6 text-gold" />
              </div>
            </div>
          </div>
        </div>

        {/* التبويبات */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('cases')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'cases' 
                  ? 'bg-green-50 text-green-700 border-b-2 border-green-500' 
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-5 h-5" />
              القضايا ({data.total_cases || 0})
            </button>
            <button
              onClick={() => setActiveTab('notary')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'notary' 
                  ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500' 
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <PenTool className="w-5 h-5" />
              خدمات الموثق ({data.total_notary || 0})
            </button>
            <button
              onClick={() => setActiveTab('consultations')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'consultations' 
                  ? 'bg-gold/20 text-gold border-b-2 border-gold' 
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <MessageSquare className="w-5 h-5" />
              الاستشارات ({data.total_consultations || 0})
            </button>
          </div>

          {/* البحث والفلترة */}
          <div className="p-4 bg-gray-50 border-b flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="بحث برقم الطلب، رقم العميل، الاسم..."
                className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
            >
              <option value="all">جميع الحالات</option>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* المحتوى */}
          <div className="p-4">
            <RequestsTable 
              requests={getCurrentRequests()} 
              showCityColumn={activeTab === 'notary'} 
            />
          </div>
        </div>
      </main>

      {/* نافذة تحديث الحالة */}
      {showStatusModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-bold text-primary">تحديث حالة الطلب</h3>
              <button
                onClick={() => setShowStatusModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">رقم الطلب:</p>
              <p className="font-mono font-semibold text-gold">{selectedRequest.request_number}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">الحالة الجديدة</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
              >
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظة (تظهر للعميل)</label>
              <textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={3}
                placeholder="أضف ملاحظة للعميل..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={updateStatus}
                disabled={updating}
                className="flex-1 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors disabled:opacity-50"
              >
                {updating ? 'جاري الحفظ...' : 'حفظ التغييرات'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة الإحالة */}
      {showAssignModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-bold text-primary flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                إحالة الطلب لموظف
              </h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">رقم الطلب:</p>
              <p className="font-mono font-semibold text-gold">{selectedRequest.request_number}</p>
              <p className="text-sm text-gray-500 mt-2">العميل: {selectedRequest.client_name}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                اختر الموظفين للإحالة (يمكن اختيار أكثر من موظف)
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                {employees.map((emp) => (
                  <label
                    key={emp.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedEmployees.includes(emp.id) 
                        ? 'bg-purple-50 border border-purple-200' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(emp.id)}
                      onChange={() => toggleEmployeeSelection(emp.id)}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-primary">{emp.full_name}</p>
                      <p className="text-xs text-gray-500">
                        {getRoleName(emp.role, emp.department)} - {emp.email}
                      </p>
                    </div>
                    {selectedEmployees.includes(emp.id) && (
                      <CheckCircle className="w-5 h-5 text-purple-600" />
                    )}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                تم اختيار {selectedEmployees.length} موظف
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظة الإحالة (تظهر للعميل)</label>
              <textarea
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                rows={2}
                placeholder="أضف ملاحظة للإحالة..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={assignRequest}
                disabled={updating || selectedEmployees.length === 0}
                className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                {updating ? 'جاري الإحالة...' : 'إحالة الطلب'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
